import { Shimmer } from '@components'
import { useDateRangeContext } from '@context'
import { formatCurrency, formatDate } from '@utils'
import React from 'react'
import { useTranslation } from 'react-i18next'
import styled, { keyframes } from 'styled-components'
import { CapitalKey, useDashboardAccountStats } from '../hooks/useDashboardAccountStats'
import { useDashboardInView } from '../hooks/useDashboardInView'
import { Card, CardHeaderMeta } from './shared/Card'

const HEALTHY_EQUITY_RATIO = 0.4

const CAPITAL_BREAKDOWN: { key: CapitalKey; side: 'capital' | 'liabilities' }[] = [
  { key: 'shareCapital', side: 'capital' },
  { key: 'retainedEarnings', side: 'capital' },
  { key: 'otherLiabilities', side: 'liabilities' },
  { key: 'payables', side: 'liabilities' }
]

export const CapitalCard: React.FC = () => {
  const [t] = useTranslation()
  const { selectedRange } = useDateRangeContext()
  const { ref, isInView } = useDashboardInView()
  const dateAt = selectedRange?.date_to
  const { data: stats, isPending: isLoading } = useDashboardAccountStats({ enabled: isInView })

  // The analytics endpoint already normalises each account type so a normal
  // balance reads positive (analytics/dataframe/account_df.py signs assets -1
  // and everything else +1). So the value is taken as it comes: a magnitude
  // would book an accumulated loss in retained earnings as positive capital
  // and overstate equity, and a flip would invert the whole card.
  const rows = CAPITAL_BREAKDOWN.map(entry => ({
    ...entry,
    value: (stats?.[entry.key] ?? 0) / 100
  }))

  const capital = rows
    .filter(row => row.side === 'capital')
    .reduce((sum, row) => sum + row.value, 0)
  const liabilities = rows
    .filter(row => row.side === 'liabilities')
    .reduce((sum, row) => sum + row.value, 0)

  const total = capital + liabilities
  const equityRatio = total > 0 ? capital / total : 0
  const isHealthy = total > 0 && equityRatio >= HEALTHY_EQUITY_RATIO

  // Bar lengths use magnitudes; the figures keep their sign.
  const largest = Math.max(Math.abs(capital), Math.abs(liabilities))
  const liabilityWidth = largest > 0 ? (Math.abs(liabilities) / largest) * 100 : 0
  const capitalWidth = largest > 0 ? (Math.abs(capital) / largest) * 100 : 0

  return (
    <FillCard
      ref={ref}
      title={t('dashboard.capital.title')}
      tooltip={t('dashboard.capital.tooltip')}
      ask={{
        prompts: t('dashboard.capital.ask.prompts', { returnObjects: true }) as string[]
      }}
      headerMeta={dateAt ? <CardHeaderMeta>{formatDate(dateAt)}</CardHeaderMeta> : null}
    >
      <Body>
        <Totals>
          <TotalBlock>
            <TotalLabel>{t('dashboard.capital.liabilities')}</TotalLabel>
            {isLoading ? (
              <Shimmer width="6rem" height="1.0625rem" />
            ) : (
              <TotalValue>{formatCurrency(liabilities)}</TotalValue>
            )}
          </TotalBlock>
          <TotalBlock $alignRight>
            <TotalLabel>{t('dashboard.capital.capital')}</TotalLabel>
            {isLoading ? (
              <Shimmer width="6rem" height="1.0625rem" />
            ) : (
              <TotalValue>{formatCurrency(capital)}</TotalValue>
            )}
          </TotalBlock>
        </Totals>

        <RatioBars>
          <RatioTrack>
            {!isLoading && <LiabilityFill style={{ width: `${liabilityWidth}%` }} />}
          </RatioTrack>
          <RatioTrack>
            {!isLoading && <CapitalFill style={{ width: `${capitalWidth}%` }} />}
          </RatioTrack>
        </RatioBars>

        <Breakdown>
          {rows.map(row => (
            <BreakdownRow key={row.key}>
              <Swatch $capital={row.side === 'capital'} />
              <BreakdownLabel>{t(`dashboard.capital.row.${row.key}`)}</BreakdownLabel>
              {isLoading ? (
                <Shimmer width="4rem" height="0.75rem" />
              ) : (
                <BreakdownValue>{formatCurrency(row.value)}</BreakdownValue>
              )}
            </BreakdownRow>
          ))}
        </Breakdown>

        <Footer>
          <FooterLabel>{t('dashboard.capital.equityRatio')}</FooterLabel>
          {isLoading ? (
            <Shimmer width="4.5rem" height="0.75rem" />
          ) : (
            <>
              <FooterValue $healthy={isHealthy}>{`${Math.round(equityRatio * 100)} %`}</FooterValue>
              <FooterPill $healthy={isHealthy}>
                {isHealthy ? t('dashboard.capital.healthy') : t('dashboard.capital.watch')}
              </FooterPill>
            </>
          )}
        </Footer>
      </Body>
    </FillCard>
  )
}

const FillCard = styled(Card)`
  flex: 1;
`

const Body = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.md}rem;
  flex: 1;
  padding: ${({ theme }) => theme.spacing.md}rem ${({ theme }) => theme.spacing.lg}rem
    ${({ theme }) => theme.spacing.lg}rem;
`

const Totals = styled.div`
  display: flex;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing.md}rem;
`

const TotalBlock = styled.div<{ $alignRight?: boolean }>`
  display: flex;
  flex-direction: column;
  text-align: ${({ $alignRight }) => ($alignRight ? 'right' : 'left')};
`

const TotalLabel = styled.span`
  font-size: ${({ theme }) => theme.fontSize.xs}rem;
  color: ${({ theme }) => theme.colors.iconGray};
`

const TotalValue = styled.span`
  font-size: ${({ theme }) => theme.fontSize.lg}rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.menuBg};
  font-variant-numeric: tabular-nums;
`

const RatioBars = styled.div`
  display: flex;
  gap: 4px;
  height: 0.875rem;
`

const RatioTrack = styled.div`
  position: relative;
  flex: 1;
  border-radius: 5px;
  background: ${({ theme }) => theme.colors.neutralGray};
  overflow: hidden;
`

const barOpen = keyframes`
  from {
    transform: scaleX(0);
  }
  to {
    transform: scaleX(1);
  }
`

const LiabilityFill = styled.div`
  position: absolute;
  right: 0;
  height: 100%;
  border-radius: 5px;
  background: ${({ theme }) => theme.dashboard.infoSoft};
  transform-origin: right;
  animation: ${barOpen} 0.6s ease-out both;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`

const CapitalFill = styled.div`
  position: absolute;
  left: 0;
  height: 100%;
  border-radius: 5px;
  background: ${({ theme }) => theme.colors.primary};
  transform-origin: left;
  animation: ${barOpen} 0.6s ease-out both;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`

const Breakdown = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.sm}rem;
`

const BreakdownRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm}rem;
`

const Swatch = styled.span<{ $capital: boolean }>`
  flex-shrink: 0;
  width: 8px;
  height: 8px;
  border-radius: 2px;
  background: ${({ theme, $capital }) =>
    $capital ? theme.colors.primary : theme.dashboard.infoSoft};
`

const BreakdownLabel = styled.span`
  flex: 1;
  min-width: 0;
  font-size: ${({ theme }) => theme.fontSize.xs}rem;
  color: ${({ theme }) => theme.dashboard.textSubtle};
`

const BreakdownValue = styled.span`
  font-size: ${({ theme }) => theme.fontSize.xs}rem;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.menuBg};
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
`

const Footer = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm}rem;
  margin-top: auto;
  padding-top: ${({ theme }) => theme.spacing.md}rem;
  border-top: 1px solid ${({ theme }) => theme.colors.neutralGray};
`

const FooterLabel = styled.span`
  flex: 1;
  font-size: ${({ theme }) => theme.fontSize.xs}rem;
  color: ${({ theme }) => theme.colors.iconGray};
`

const FooterValue = styled.span<{ $healthy: boolean }>`
  font-size: ${({ theme }) => theme.fontSize.xs}rem;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  color: ${({ theme, $healthy }) =>
    $healthy ? theme.dashboard.positive : theme.dashboard.caution};
`

const FooterPill = styled.span<{ $healthy: boolean }>`
  padding: 0.125rem ${({ theme }) => theme.spacing.sm}rem;
  border-radius: 999px;
  font-size: ${({ theme }) => theme.fontSize.px11}rem;
  font-weight: 700;
  white-space: nowrap;
  background: ${({ theme, $healthy }) =>
    $healthy ? theme.dashboard.positiveTint : theme.dashboard.cautionTint};
  color: ${({ theme, $healthy }) =>
    $healthy ? theme.dashboard.positive : theme.dashboard.caution};
`
